/**
 * Settings Store
 *
 * Manages user preferences with localStorage persistence.
 * Includes theme, user profile, assistant settings, and local context.
 */

import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { api } from '@/services/api';
import { isValidTimeZone } from '@/lib/timestamps';
import { DEFAULT_THEME, type Theme } from './themeDefaults';

export type { Theme } from './themeDefaults';

interface SettingsState {
  // User profile
  userName: string;

  // Appearance
  theme: Theme;

  // Local context. Also the display zone for chat timestamps. Stored per
  // user in this browser; there is no server-side user settings store.
  contextTimezone: string;

  // Actions
  setUserName: (name: string) => void;
  setUserNameFromServer: (name: string) => void;
  setTheme: (theme: Theme) => void;
  setContextTimezone: (timezone: string) => void;
  loadUserTimezone: (userId: string | number) => void;
  resetUserSettings: () => void;
  resetSettings: () => void;
}

function getDefaultTimezone(): string {
  if (typeof Intl === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

function timezoneStorageKey(userId: string | number): string {
  return `telcoos-timezone:${userId}`;
}

// Storage can be unavailable (privacy mode, disabled storage, quota). A
// preference must never break the caller, so storage errors are logged and
// the store keeps working in memory. Persisted settings use this storage too,
// because the persist middleware writes on every state change.
const safeLocalStorage = {
  getItem: (name: string): string | null => {
    try {
      return localStorage.getItem(name);
    } catch (err) {
      console.warn(`Failed to read ${name} from storage:`, err);
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      localStorage.setItem(name, value);
    } catch (err) {
      console.warn(`Failed to save ${name} to storage:`, err);
    }
  },
  removeItem: (name: string): void => {
    try {
      localStorage.removeItem(name);
    } catch (err) {
      console.warn(`Failed to remove ${name} from storage:`, err);
    }
  },
} satisfies StateStorage;

// The signed-in user whose time zone the store holds.
let timezoneUserId: string | number | null = null;

const defaultSettings = {
  userName: '',
  theme: DEFAULT_THEME,
  contextTimezone: getDefaultTimezone(),
};

// Apply theme to document
function applyTheme(theme: Theme) {
  const root = document.documentElement;

  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  } else {
    root.classList.toggle('dark', theme === 'dark');
  }
}

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const stored = localStorage.getItem('telcoos-settings');
    if (stored) {
      try {
        const settings = JSON.parse(stored);
        if (settings.state?.theme === 'system') {
          applyTheme('system');
        }
      } catch {
        // Ignore parse errors
      }
    }
  });
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...defaultSettings,

      setUserName: (userName) => {
        set({ userName });
        // Persist to server (fire and forget - optimistic update)
        api.auth.updateDisplayName(userName).catch((err) => {
          console.error('Failed to save display name:', err);
        });
      },

      // Set userName from server response (doesn't call API)
      setUserNameFromServer: (userName) => set({ userName }),

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },

      setContextTimezone: (contextTimezone) => {
        if (!isValidTimeZone(contextTimezone)) return;
        if (timezoneUserId !== null) {
          safeLocalStorage.setItem(timezoneStorageKey(timezoneUserId), contextTimezone);
        }
        set({ contextTimezone });
      },

      loadUserTimezone: (userId) => {
        timezoneUserId = userId;
        const stored = safeLocalStorage.getItem(timezoneStorageKey(userId));
        set({
          contextTimezone: stored && isValidTimeZone(stored) ? stored : getDefaultTimezone(),
        });
      },

      // Reset only user-specific settings (call on logout to prevent data leakage)
      resetUserSettings: () => {
        timezoneUserId = null;
        set({
          userName: '',
          contextTimezone: getDefaultTimezone(),
        });
      },

      resetSettings: () => {
        applyTheme(defaultSettings.theme);
        set(defaultSettings);
      },
    }),
    {
      name: 'telcoos-settings',
      storage: createJSONStorage(() => safeLocalStorage),
      // Don't persist userName - it's stored server-side per user.
      // contextTimezone is stored per user under telcoos-timezone:<user id>.
      partialize: (state) => {
        const { userName: _userName, contextTimezone: _contextTimezone, ...rest } = state;
        return rest;
      },
      // Older saved settings can hold the removed claudeTone field. Drop it.
      merge: (persisted, current) => {
        const { claudeTone: _claudeTone, ...rest } = (persisted ?? {}) as Partial<SettingsState> & { claudeTone?: unknown };
        return { ...current, ...rest };
      },
      onRehydrateStorage: () => (state) => {
        // Apply theme on rehydration
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);

// Initialize theme and accent color on load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('telcoos-settings');
  if (stored) {
    try {
      const settings = JSON.parse(stored);
      if (settings.state?.theme) {
        applyTheme(settings.state.theme);
      }
    } catch {
      applyTheme(DEFAULT_THEME);
    }
  } else {
    applyTheme(DEFAULT_THEME);
  }
}
